import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { useGlobalStore } from '@atlas/shared-state';
import type { ApiResponse, PagedResponse, PagedRequest } from '@atlas/shared-types';

const API_BASE_URL = process.env.NX_API_BASE_URL || `${window.location.protocol}//${window.location.hostname}:5050/api`;

/**
 * Structured error thrown by the api-client interceptor.
 *
 * Subclasses `Error` so every existing `catch (err) { err.message }` /
 * `err instanceof Error` consumer keeps working unchanged. Adds:
 *   - `field`  — the server-side field name the error applies to (e.g. "code"),
 *                when the backend includes it in `ApiResponse.errors[0].field`.
 *   - `status` — the HTTP status code the server returned, when available.
 *   - `errors` — the full `ApiResponse.errors[]` array when the backend returns
 *                multiple structured errors (e.g. ADR-010 PoMergeValidationException
 *                emits one entry per offending PO id). Empty when only a single
 *                error or non-ApiResponse shape was returned.
 *
 * Consumers that want field-level UX (e.g. surfacing a 409 on the Code input
 * instead of a toast) should do `if (err instanceof ApiClientError && err.field === 'code')`.
 *
 * Consumers that want per-row error UX (e.g. ADR-010 multi-PO RR merge: highlight
 * each offending PO chip) should iterate `err.errors`. Each entry retains the
 * server's `code`/`message`/`field` shape so callers can route by code prefix
 * (e.g. `code.startsWith('po_merge_')`) without touching the interceptor.
 *
 * Transport-level diagnostics (populated on the timeout / network branches,
 * where there is NO response to inspect):
 *   - `code`      — the axios error code that classified the failure, e.g.
 *                   'ECONNABORTED' for a client-side timeout.
 *   - `method`    — the HTTP verb of the aborted request, upper-cased ('DELETE').
 *   - `url`       — the request path as the caller issued it ('/ismc/sales-sheets');
 *                   the api-client baseURL is not folded in.
 *   - `timeoutMs` — the limit that actually fired, i.e. the per-call override when
 *                   one was passed, otherwise the client default.
 *
 * These exist because a client-side abort leaves nothing in DevTools: the request
 * never received a response, so the Network tab reports "failed to load response
 * data — no data found for resource with given identifier". Everything knowable
 * about the failure lives on `error.config`, so the interceptor lifts it onto the
 * error (and logs it) instead of throwing a context-free string.
 */
export interface ApiClientErrorEntry {
  code?: string;
  message: string;
  field?: string;
}

export interface ApiClientErrorOptions {
  field?: string;
  status?: number;
  errors?: ApiClientErrorEntry[];
  code?: string;
  method?: string;
  url?: string;
  timeoutMs?: number;
}

export class ApiClientError extends Error {
  readonly field?: string;
  readonly status?: number;
  readonly errors: ApiClientErrorEntry[];
  readonly code?: string;
  readonly method?: string;
  readonly url?: string;
  readonly timeoutMs?: number;

  constructor(message: string, opts?: ApiClientErrorOptions) {
    super(message);
    this.name = 'ApiClientError';
    this.field = opts?.field;
    this.status = opts?.status;
    this.errors = opts?.errors ?? [];
    this.code = opts?.code;
    this.method = opts?.method;
    this.url = opts?.url;
    this.timeoutMs = opts?.timeoutMs;
    // Preserve the prototype chain when transpiled to ES5 targets.
    Object.setPrototypeOf(this, ApiClientError.prototype);
  }
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
});

// Request interceptor: attach Bearer token & fix Content-Type for FormData
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useGlobalStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Let Axios auto-set Content-Type with boundary for FormData
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

// Response interceptor: handle 401 with token refresh, plus 403 with a
// one-shot refresh-and-retry to handle stale-JWT cases (admin granted a
// new perm server-side; user's existing token doesn't carry it yet).
let isRefreshing = false;
let failedQueue: Array<{ resolve: (value: unknown) => void; reject: (reason?: unknown) => void }> = [];

const processQueue = (error: unknown, token?: string) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

/**
 * Calls `/auth/refresh` once with the stored refresh token, installs the
 * new access token in the global store + localStorage, and resolves to the
 * new access token. Concurrent callers share the same in-flight refresh
 * via `isRefreshing` + `failedQueue`. Throws on missing refresh token or
 * non-200 from `/auth/refresh`. Caller decides what to do on failure
 * (401 path resets the session and redirects to /login; 403 path swallows
 * the error and falls through to the friendly forbidden message).
 */
async function refreshAccessTokenOnce(): Promise<string> {
  if (isRefreshing) {
    return new Promise<string>((resolve, reject) => {
      failedQueue.push({
        resolve: (token) => resolve(token as string),
        reject,
      });
    });
  }

  isRefreshing = true;
  try {
    const refreshToken = localStorage.getItem('atlas_refresh_token');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }
    const response = await axios.post(`${API_BASE_URL}/auth/refresh`, { refreshToken });
    const { accessToken, refreshToken: newRefreshToken } = response.data;
    useGlobalStore.getState().setAccessToken(accessToken);
    localStorage.setItem('atlas_refresh_token', newRefreshToken);
    processQueue(null, accessToken);
    return accessToken;
  } catch (refreshError) {
    processQueue(refreshError);
    throw refreshError;
  } finally {
    isRefreshing = false;
  }
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (originalRequest.url?.includes('/auth/login')) {
        return Promise.reject(error);
      }
      // RR Module Spec v1.0 §D #6 (R5): a 401 from /auth/verify-password
      // means "wrong password / locked account" — NOT "stale JWT". The
      // caller (PasswordConfirmModal) needs to see the 401 directly so it
      // can show "Incorrect password" inline. Triggering a refresh-and-
      // retry here would silently re-issue the same wrong-password request
      // and double up the audit-log entries on the BE.
      if (originalRequest.url?.includes('/auth/verify-password')) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;
      try {
        await refreshAccessTokenOnce();
        return apiClient(originalRequest);
      } catch (refreshError) {
        // 401 = invalid token. Refresh failure means session is dead —
        // reset state and kick to login.
        useGlobalStore.getState().reset();
        localStorage.removeItem('atlas_refresh_token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    const responseStatus = error.response?.status;

    if (responseStatus === 403 && !originalRequest._retry) {
      // Stale-JWT pattern: an admin may have granted a new perm
      // server-side, but the user's existing token doesn't carry it
      // yet. Refresh once and retry. If the replayed request is still
      // 403, OR the refresh fails, fall through to the friendly error
      // below — refresh-token validity is independent of perm denial,
      // so don't reset session.
      const isAuthEndpoint =
        originalRequest.url?.includes('/auth/login') ||
        originalRequest.url?.includes('/auth/refresh');
      if (!isAuthEndpoint) {
        originalRequest._retry = true;
        try {
          await refreshAccessTokenOnce();
          return apiClient(originalRequest);
        } catch {
          // Swallow — fall through to the friendly 403 error below.
        }
      }
    }

    // Handle 403 Forbidden — bare response with no body from Forbid()
    if (responseStatus === 403) {
      return Promise.reject(
        new ApiClientError(
          'Access denied. You do not have permission to perform this action.',
          { status: 403 },
        ),
      );
    }

    // Handle 404 Not Found
    if (responseStatus === 404) {
      return Promise.reject(
        new ApiClientError(
          'The requested resource was not found. The service may be unavailable.',
          { status: 404 },
        ),
      );
    }

    // Handle network / timeout errors.
    //
    // Both branches abort client-side, so there is no response body anywhere —
    // DevTools shows "failed to load response data" and the Network tab is a dead
    // end. `error.config` is the only surviving evidence, so lift it onto the
    // thrown error and log it: that console line is the ONLY trace a timeout leaves.
    const failedConfig = error.config;
    const requestMethod =
      typeof failedConfig?.method === 'string' && failedConfig.method.length > 0
        ? failedConfig.method.toUpperCase()
        : undefined;
    const requestUrl =
      typeof failedConfig?.url === 'string' && failedConfig.url.length > 0
        ? failedConfig.url
        : undefined;
    const requestTimeoutMs =
      typeof failedConfig?.timeout === 'number' && failedConfig.timeout > 0
        ? failedConfig.timeout
        : undefined;
    // "DELETE /ismc/sales-sheets" when both are known; whichever half we have otherwise.
    const requestLabel = [requestMethod, requestUrl].filter((part) => part !== undefined).join(' ');

    if (error.code === 'ECONNABORTED') {
      // State the limit that fired: a 60s timeout on a bulk delete is a slow
      // server, not a flaky link, and QA must be able to tell those apart.
      const limitPart = requestTimeoutMs !== undefined ? ` after ${requestTimeoutMs}ms` : '';
      const targetPart = requestLabel.length > 0 ? `: ${requestLabel}` : '';
      const timeoutMessage =
        limitPart.length > 0 || targetPart.length > 0
          ? `Request timed out${limitPart}${targetPart}`
          : 'Request timed out. Please check your connection and try again.';

      console.error('[api-client] request aborted (timeout)', {
        code: error.code,
        method: requestMethod,
        url: requestUrl,
        baseURL: failedConfig?.baseURL,
        timeoutMs: requestTimeoutMs,
      });

      return Promise.reject(
        new ApiClientError(timeoutMessage, {
          code: error.code,
          method: requestMethod,
          url: requestUrl,
          timeoutMs: requestTimeoutMs,
        }),
      );
    }
    if (!error.response) {
      const targetPart = requestLabel.length > 0 ? ` (${requestLabel})` : '';
      return Promise.reject(
        new ApiClientError(`Network error. Unable to reach the server.${targetPart}`, {
          code: error.code,
          method: requestMethod,
          url: requestUrl,
        }),
      );
    }

    // Non-JSON response body: pass through the original error untouched
    if (error.response?.data && typeof error.response.data !== 'object') {
      return Promise.reject(error);
    }

    // Extract user-friendly error message from response body
    if (error.response?.data) {
      const data = error.response.data as Record<string, unknown>;

      // Format 1: ApiResponse { errors: [{ code?, message, field? }] }
      // Preserve `field` so consumers can surface field-level UX (e.g. 409 on Code).
      // Also preserve the full array so consumers needing per-row error UX (e.g.
      // ADR-010 multi-PO RR merge: highlight each offending PO chip) can iterate.
      if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
        const rawEntries = data.errors as Array<{ code?: string; message?: string; field?: string }>;
        const firstError = rawEntries[0];
        if (firstError?.message) {
          const allEntries: ApiClientErrorEntry[] = rawEntries
            .filter((e): e is { code?: string; message: string; field?: string } => typeof e?.message === 'string')
            .map((e) => ({
              code: typeof e.code === 'string' && e.code.length > 0 ? e.code : undefined,
              message: e.message,
              field: typeof e.field === 'string' && e.field.length > 0 ? e.field : undefined,
            }));
          return Promise.reject(
            new ApiClientError(firstError.message, {
              field: typeof firstError.field === 'string' && firstError.field.length > 0
                ? firstError.field
                : undefined,
              status: responseStatus,
              errors: allEntries,
            }),
          );
        }
      }

      // Format 2: ASP.NET ProblemDetails { title, errors: { FieldName: ["msg"] } }
      if (data.errors && typeof data.errors === 'object' && !Array.isArray(data.errors)) {
        const fieldErrors = data.errors as Record<string, string[]>;
        const messages: string[] = [];
        let firstField: string | undefined;
        for (const [field, msgs] of Object.entries(fieldErrors)) {
          if (Array.isArray(msgs)) {
            for (const msg of msgs) {
              // Strip "The X field is required." → "X is required."
              const cleaned = msg.replace(/^The\s+\S+\s+field\s+/, `${field}: `);
              messages.push(cleaned);
              if (!firstField) firstField = field;
            }
          }
        }
        if (messages.length > 0) {
          return Promise.reject(
            new ApiClientError(messages.join('. '), {
              field: firstField,
              status: responseStatus,
            }),
          );
        }
      }

      // Format 2b: { error: "msg" } (singular — used by controller BadRequest responses)
      if (typeof data.error === 'string' && data.error) {
        return Promise.reject(new ApiClientError(data.error, { status: responseStatus }));
      }

      // Format 3: ApiResponse { errors: ["msg"] } (string array)
      if (typeof data.message === 'string' && data.message) {
        return Promise.reject(new ApiClientError(data.message, { status: responseStatus }));
      }
      // Format 4: ASP.NET ProblemDetails `detail` (RFC9110) — the occurrence-specific
      // explanation. The shared GlobalExceptionHandler populates it with the exception
      // text outside Production, so prefer it over the generic `title` ("An unexpected
      // error occurred.") to make the real server error visible in dev/staging toasts.
      if (typeof data.detail === 'string' && data.detail) {
        return Promise.reject(new ApiClientError(data.detail, { status: responseStatus }));
      }
      if (typeof data.title === 'string' && data.title !== 'One or more validation errors occurred.') {
        return Promise.reject(new ApiClientError(data.title, { status: responseStatus }));
      }
    }

    // Fallback: include status code for debugging
    const status = responseStatus ?? 'unknown';
    return Promise.reject(
      new ApiClientError(`Request failed (HTTP ${status}). Please try again.`, {
        status: responseStatus,
      }),
    );
  }
);

// Helper functions
//
// `params` is `object` (any non-null object) rather than `Record<string, unknown>`
// so callers can pass typed query interfaces (e.g. `StockMovementParams`) without
// the `as Record<string, unknown>` cast. The downstream axios call accepts `any`
// for params; serialization handles primitives, arrays, dates, etc.
//
// `config` mirrors `post`/`del`: a per-call `timeout` override for GET endpoints
// whose server-side work legitimately outruns the 10s client default (e.g. the
// Supplier Detailed Report's legacy-api → SQL-Server round trip on a full-month,
// busy-supplier range). Optional, so existing `get(url)` / `get(url, params)`
// callers are unaffected.
export async function get<T>(url: string, params?: object, config?: { timeout?: number }): Promise<T> {
  const response = await apiClient.get<T>(url, { params, ...config });
  return response.data;
}

export async function post<T>(url: string, data?: unknown, config?: { timeout?: number }): Promise<T> {
  const response = await apiClient.post<T>(url, data, config);
  return response.data;
}

export async function put<T>(url: string, data?: unknown): Promise<T> {
  const response = await apiClient.put<T>(url, data);
  return response.data;
}

export async function patch<T>(url: string, data?: unknown): Promise<T> {
  const response = await apiClient.patch<T>(url, data);
  return response.data;
}

// `data` is optional — most DELETEs are id-in-path, but bulk endpoints
// (e.g. DELETE /hr/employees/bulk) need a request body.
//
// `config` mirrors `post` above: a per-call `timeout` override for endpoints
// whose server-side work legitimately outruns the 10s client default (e.g. a
// bulk delete that reverses hundreds of stock movements in one transaction).
// Both params stay optional, so existing `del(url)` / `del(url, body)` callers
// are unaffected.
export async function del<T>(
  url: string,
  data?: unknown,
  config?: { timeout?: number },
): Promise<T> {
  const response = await apiClient.delete<T>(
    url,
    data !== undefined ? { ...config, data } : config,
  );
  return response.data;
}

export async function getPaged<T>(url: string, request: PagedRequest): Promise<PagedResponse<T>> {
  const response = await apiClient.get<PagedResponse<T>>(url, { params: request });
  return response.data;
}
